import React from "react";
import { ShieldCheck, Sparkles, HeartHandshake, MapPin } from "lucide-react";
import { Container, Button, SectionHeading, Eyebrow } from "@/components/primitives";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageHero, CTASection, TrustMarquee } from "@/components/sections";
import { useSeo } from "@/lib/seo";
import { team, business } from "@/data/site";

const protocols = [
  { icon: ShieldCheck, title: "Hospital-grade sterilization", body: "Autoclave sterilization of all instruments, single-use disposables and surface disinfection between every patient — following OSHA and CDC guidelines." },
  { icon: Sparkles, title: "Clean air, clean rooms", body: "HEPA-grade air filtration and thoroughly disinfected operatories keep our Hell's Kitchen studio pristine and safe." },
  { icon: HeartHandshake, title: "Comfort-first culture", body: "Sedation options, noise-canceling headphones and unhurried appointments make anxious visits genuinely calm." },
];

export default function About() {
  useSeo({
    title: "About & Our Team | Meridian Dental Studio, Hell's Kitchen NYC",
    description: "Meet the dentists, orthodontist and hygienists behind Meridian Dental Studio in Hell's Kitchen, Manhattan. Our story, philosophy of care and sterilization standards.",
    path: "/about",
  });

  return (
    <>
      <PageHero
        eyebrow="About Meridian"
        title="Museum-level craft, chairside warmth."
        intro="Meridian Dental Studio was founded on a simple belief: top-tier dentistry should feel calm, honest and genuinely human. We bring precision technology and gentle care together in the heart of Hell's Kitchen."
        image="https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
        imageAlt="Meridian Dental Studio reception and waiting area"
      />

      <section className="py-16 sm:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-3">
            {[
              { label: "Our story", text: "What began as one dentist's frustration with cold, rushed care became a studio designed around the patient — from the moment you walk in to the follow-up text after you leave." },
              { label: "Our philosophy", text: "We treat people, not teeth. That means listening first, explaining everything, and never recommending work you don't need. Trust is the treatment plan." },
              { label: "Our mission", text: "To give every New Yorker access to elevated, anxiety-free dental care — comprehensive, transparent and delivered with the craft of a fine studio." },
            ].map((b, i) => (
              <Reveal key={b.label} delay={i * 0.08}>
                <Eyebrow>{b.label}</Eyebrow>
                <p className="mt-5 text-lg font-light leading-relaxed text-[var(--fg)]/85">{b.text}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <TrustMarquee />

      <section className="py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="Meet the doctors & team" title="The people behind your smile." intro="Credentialed clinicians with advanced training and a genuinely gentle chairside manner." />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2">
            {team.map((m) => (
              <StaggerItem key={m.name}>
                <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] sm:flex-row" data-testid={`team-${m.name.split(" ")[1] || m.name}`}>
                  <div className="relative aspect-[4/5] w-full overflow-hidden sm:w-2/5">
                    <img src={m.photo} alt={`${m.name}, ${m.role}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{m.name}</h3>
                    <p className="mt-1 text-sm font-medium text-[var(--primary)]">{m.role}</p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted-fg)]">{m.credentials}</p>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-[var(--muted-fg)]">{m.bio}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {m.specialties.map((s) => (
                        <span key={s} className="rounded-full border border-[var(--border)] px-3 py-1 text-xs text-[var(--fg)]/70">{s}</span>
                      ))}
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </section>

      <section className="bg-[var(--surface)] py-24 sm:py-32">
        <Container>
          <SectionHeading eyebrow="Clean & safe" title="Sterilization standards NYC patients can trust." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {protocols.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8">
                  <p.icon size={26} className="text-[var(--primary)]" />
                  <h3 className="mt-5 font-heading text-2xl font-normal text-[var(--fg)]">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--muted-fg)]">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col items-start gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-8 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <MapPin size={22} className="mt-1 shrink-0 text-[var(--primary)]" />
                <p className="text-sm leading-relaxed text-[var(--fg)]/85">
                  <span className="font-medium text-[var(--fg)]">Proudly local.</span> We're active in the Hell's Kitchen community — sponsoring neighborhood events and welcoming patients from across Midtown West, Chelsea and Hudson Yards.
                </p>
              </div>
              <Button to="/contact" variant="outline" className="shrink-0" data-testid="about-visit-btn">Visit our studio</Button>
            </div>
          </Reveal>
        </Container>
      </section>

      <CTASection eyebrow="Meet us in person" title="Come see the difference for yourself." />
    </>
  );
}
